import { useMemo } from 'react';
import { BRAND, RESTRICTED_COUNTRY_NAMES } from '@platform/shared';
import type { GeoBlock } from '../lib/geo';

const NAME: Record<string, { short: string; inText: string }> = {
  DE: { short: 'Germany', inText: 'Germany' },
  US: { short: 'United States', inText: 'the United States' },
  IL: { short: 'Israel', inText: 'Israel' },
  CY: { short: 'Cyprus', inText: 'Cyprus' },
};

/** Support reference: country and the UTC minute the client was blocked, e.g. GEO-DE-2610111432. */
function reference(country: string) {
  const d = new Date().toISOString();
  return `GEO-${country || 'XX'}-${d.slice(2, 4)}${d.slice(5, 7)}${d.slice(8, 10)}${d.slice(11, 13)}${d.slice(14, 16)}`;
}

/**
 * Full-screen popup for clients in a restricted country. It can't be dismissed: the app behind it is
 * inert, so the only way out is support.
 */
export function GeoBlockDialog({ country, basis }: GeoBlock) {
  const ref = useMemo(() => reference(country), [country]);
  const n = NAME[country];
  const where = n?.inText ?? 'your country';
  const consequence = "you can't log in, open an account, deposit, withdraw or trade.";
  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-scrim backdrop-blur-md sm:items-center sm:p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="geo-title"
        aria-describedby="geo-body"
        className="flex max-h-dvh w-full flex-col gap-5 overflow-y-auto rounded-t-[28px] border-t border-line-strong bg-panel px-5 pt-7 pb-[max(32px,env(safe-area-inset-bottom))] text-fg shadow-[0_24px_80px_var(--shadow)] sm:max-w-[520px] sm:gap-6 sm:rounded-[28px] sm:border sm:p-10"
      >
        <div className="flex items-center gap-3 sm:justify-between">
          <div className="grid size-12 shrink-0 place-items-center rounded-full bg-down-bg text-down sm:size-14">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M3 12h18" />
              <path d="M12 3a14 14 0 0 1 0 18" />
              <path d="M12 3a14 14 0 0 0 0 18" />
              <path d="M4.5 4.5l15 15" strokeWidth="2.2" />
            </svg>
          </div>
          <span className="rounded-full bg-down-bg px-3 py-1.5 text-[13px] font-semibold text-down">Access restricted</span>
        </div>

        <div className="flex flex-col gap-2.5 sm:gap-3">
          <h1 id="geo-title" className="m-0 text-[25px] leading-[1.15] font-semibold tracking-[-0.02em] sm:text-[30px] sm:tracking-[-0.025em]">
            {BRAND.name} isn't available in {where}
          </h1>
          <p id="geo-body" className="m-0 text-[15px] leading-[1.55] text-muted sm:text-[16px]">
            We can't provide our services to residents of, or clients connecting from, {RESTRICTED_COUNTRY_NAMES}.{' '}
            {basis === 'ip' ? `While you're in ${where} ${consequence}` : basis === 'residence' ? `As a resident of ${where} ${consequence}` : `From ${where} ${consequence}`}
          </p>
        </div>

        <dl className="m-0 flex flex-col rounded-2xl bg-raised text-[14px] sm:rounded-[18px]">
          {n && (
            <>
              <div className="flex items-center justify-between gap-4 px-4 py-[13px] sm:px-[18px] sm:py-3.5">
                <dt className="text-muted">{basis === 'residence' ? 'Country of residence' : 'Your location'}</dt>
                <dd className="m-0 font-semibold">{n.short}</dd>
              </div>
              <div className="h-px bg-line" />
            </>
          )}
          <div className="flex items-center justify-between gap-4 px-4 py-[13px] sm:px-[18px] sm:py-3.5">
            <dt className="text-muted">Reference</dt>
            <dd className="num m-0 font-mono font-medium">{ref}</dd>
          </div>
        </dl>

        <div className="flex flex-col gap-3">
          <a
            href={`mailto:${BRAND.supportEmail}?subject=${encodeURIComponent(`Access restricted (${ref})`)}`}
            className="flex h-[52px] items-center justify-center rounded-full bg-fg text-base font-semibold text-bg no-underline sm:h-14"
          >
            Contact support
          </a>
          <p className="m-0 text-center text-[13px] leading-normal text-muted">
            {basis === 'residence'
              ? 'Moved to a country we serve? Contact support and quote the reference above.'
              : 'Live in a country we serve and travelling? Contact support and quote the reference above.'}
          </p>
        </div>
      </div>
    </div>
  );
}
